import React, { useState } from 'react';
import {
  Alert, Badge, Button, Card, FileButton, Group, Stack, Text, Title, Tooltip,
} from '@mantine/core';
import { IconAlertCircle, IconDownload, IconFileSpreadsheet, IconUpload } from '@tabler/icons-react';

import { getArtifactUrl } from '../api_scripts/solnStudioClient';
import { applicableDeliverables, DESIGN_ARTIFACT_TYPES } from '../utils/designDeliverables';
import { timeAgo, shortBy } from '../utils/format';
import PanelStub from '../components/PanelStub';

// The document workspace: persisted, versioned deliverables as a list with status, download, and a
// per-document "Upload revised" that routes the marked-up file back into the design.
export default function DocumentsPanel({ project, designModel, hasModel, getApiToken, docBusy, onRefresh, onUploadRevised }) {
  const [dlError, setDlError] = useState(null);
  const deliverables = designModel ? applicableDeliverables(designModel) : [];
  const byId = new Map((project.artifacts || []).filter((a) => DESIGN_ARTIFACT_TYPES.includes(a.type)).map((a) => [a.id, a]));
  const currentVersion = project.designModelVersion || 0;

  const download = async (artifact) => {
    setDlError(null);
    try {
      const { url } = await getArtifactUrl(project.projectId, artifact.id, getApiToken);
      window.open(url, '_blank');
    } catch (err) { setDlError(err.message); }
  };

  if (!hasModel) {
    return <PanelStub title="Documents" step="Design"
      blurb="Once you generate a design, your deliverables (DDD worksheet, customer worksheet, and — for a large NLU bot — the NLU design worksheet) appear here as downloadable, versioned documents." />;
  }

  return (
    <Card withBorder padding="lg" radius="md">
      <Group justify="space-between" mb="xs">
        <div>
          <Title order={4}>Documents</Title>
          <Text size="sm" c="dimmed">
            Download a document, work it through with the customer, then Upload revised — its changes feed
            back into the design. Documents refresh automatically each time you Refine.
          </Text>
        </div>
        <Button size="xs" variant="light" color="orange" loading={docBusy === '*'} onClick={() => onRefresh()}>
          Refresh all
        </Button>
      </Group>
      {dlError && <Alert color="red" icon={<IconAlertCircle size={16} />} mb="sm">{dlError}</Alert>}

      <Stack gap="xs">
        {deliverables.map((d) => {
          const artifact = byId.get(d.id);
          const stale = artifact && artifact.version !== currentVersion;
          const busy = docBusy === d.id || docBusy === '*';
          return (
            <Card key={d.id} withBorder padding="sm" radius="sm">
              <Group justify="space-between" wrap="nowrap" align="flex-start">
                <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                  <IconFileSpreadsheet size={20} />
                  <div style={{ minWidth: 0 }}>
                    <Group gap="xs">
                      <Text fw={500} size="sm">{d.label}</Text>
                      {!artifact && <Badge size="xs" variant="light" color="gray">Not generated</Badge>}
                      {artifact && !stale && <Badge size="xs" variant="light" color="green">Current · v{artifact.version}</Badge>}
                      {artifact && stale && <Badge size="xs" variant="light" color="yellow">Out of date · made at v{artifact.version} (now v{currentVersion})</Badge>}
                      {artifact && <Badge size="xs" variant="outline" color="gray">{artifact.revision > 1 ? `Updated ×${artifact.revision}` : 'Initial'}</Badge>}
                    </Group>
                    <Text size="xs" c="dimmed" lineClamp={2}>{d.description}</Text>
                    {artifact && <Text size="xs" c="dimmed">Generated {timeAgo(artifact.generatedAt)}{artifact.generatedBy ? ` · ${shortBy(artifact.generatedBy)}` : ''}</Text>}
                  </div>
                </Group>
                <Group gap="xs" wrap="nowrap">
                  {artifact && (
                    <Button size="xs" variant="subtle" color="orange" leftSection={<IconDownload size={14} />} onClick={() => download(artifact)}>
                      Download
                    </Button>
                  )}
                  {(!artifact || stale) && (
                    <Button size="xs" variant="light" color="orange" loading={busy} onClick={() => onRefresh([d.id])}>
                      {artifact ? 'Refresh' : 'Generate'}
                    </Button>
                  )}
                  <FileButton onChange={(file) => onUploadRevised(d, file)} accept=".xlsx">
                    {(props) => (
                      <Tooltip label="Upload your edited copy — its changes feed back into the design" withArrow>
                        <Button {...props} size="xs" variant="subtle" color="gray" leftSection={<IconUpload size={14} />}>
                          Upload revised
                        </Button>
                      </Tooltip>
                    )}
                  </FileButton>
                </Group>
              </Group>
            </Card>
          );
        })}
      </Stack>
    </Card>
  );
}
